import React, { useState, useEffect, useMemo } from "react";
import setting from '../../config.json';
import appURL from '../../appURL.json';
import TauriLoader from "../TauriLoader/TauriLoader";
import './TauriHistoricalTableVisual.css';
import TauriCustomPaganation from "../TauriCustomPaganation/TauriCustomPaganation";
import { AccessComponentFactory } from "../../visualizations/AccessComponentFactory";
import { useFilterContext } from "../TauriGlobalContext/TauriGlobalContext";
import { useParams } from "react-router-dom";
import TauriSearchResultDetailModal from "../TauriSearchResultDetailModal/TauriSearchResultDetailModal";

const TauriHistoricalTableVisual = React.memo(({ }) => {
  const { id } = useParams();

  const [onLoadDataList, setOnLoadDataList] = useState();
  const [dataList, setDataList] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [displayPerPage, setDisplayPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = useMemo(() => Math.ceil(totalItems / displayPerPage), [totalItems, displayPerPage]);

  const FromYear = 2022;
  const ToYear = 2025;

  const getPermission = localStorage.getItem('permission');
  const decodedPermission = JSON.parse(atob(getPermission));

  const access = decodedPermission?.DataScope?.Access;
  const colorscheme = AccessComponentFactory('colorScheme', access ? access?.toString() : '-1');

  const palettes = colorscheme.palettes.find(pal => pal.id === 'palette21').ranges;

  const [columnWidths, setColumnWidths] = useState({});

  const handleMouseDown = (index, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[index] || 150; // Default width

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(startWidth + moveEvent.clientX - startX, 50); // Min width = 50px
      setColumnWidths((prev) => ({ ...prev, [index]: newWidth }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const [tableConfig, settableConfig] = useState({
    binding: {
      data: {
        columns: [
          { name: "Product Code", columnName: "ItemCode" },
          { name: "Product Name", columnName: "ItemName" }
        ]
      },
      context: {
        point: {
          onClick: {
            selectedValueColumn: "id"
          }
        }
      }
    }
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        await fetchOnLoadData();
        await fetchData();
      } catch (error) {
        console.error("Error during initial data fetch:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');

    const requestBody = {
      "id": "N75",
      "params": [
        { "key": "FromYear", "value": FromYear.toString() },
        { "key": "ToYear", "value": ToYear.toString() },
        { "key": "ConfigTypeCode", "value": "Y2025" }
      ]
    };

    try {
      const response = await fetch(appURL.API_GetData, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.error('Failed to fetch data:', response.status);
        setDataList([]);
        return;
      }

      const data = await response.json();
      const parsedData = JSON.parse(data.result.data);
      setTotalItems(parsedData.length);
      setDataList(parsedData);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchOnLoadData = async () => {
    try {
      const storedFilter = localStorage.getItem("filterData");
      const parseStoredFilter = JSON.parse(storedFilter);

      const fromYear = FromYear; // Converts to integer (base 10)
      const toYear = ToYear; // Assuming ToYear needs the same conversion

      const years = [];
      for (let year = fromYear; year <= toYear; year++) {
        years.push({ Year: year });
      }

      if (years?.length > 0) {
        // Initialize the columns array with the base columns (without any groups)
        const updatedColumns = [...tableConfig.binding.data.columns];

        // Clear the existing groups by filtering out any objects with the 'group' property
        const columnsWithoutGroups = updatedColumns.filter(column => !column.group);

        // Reinitialize the columns with no groups
        updatedColumns.length = 0; // Clear the array completely

        // Push the base columns (if any) and then add the new groups
        updatedColumns.push(...columnsWithoutGroups);

        years.map(item => {
          const year = Object.values(item)[0];

          updatedColumns.push({
            group: {
              name: `${year}`,
              columns: [
                { name: "Sales", columnName: `Sales${year}`, align: "right" },
                ...(year !== 2022 ? [{ name: "Growth", columnName: `Growth${year}`, align: "right", percent: true }] : [])
                // { name: "Growth", columnName: `Growth${year}`, align: "right", percent: true }
              ]
            },
          });

        });

        // Update the table configuration with the new columns
        settableConfig((prevConfig) => ({
          ...prevConfig,
          binding: {
            ...prevConfig.binding,
            data: {
              ...prevConfig.binding.data,
              columns: updatedColumns
            }
          }
        }));
      }

    } catch (error) {
      console.log(error);
    }
  };

  const handleSort = (columnName) => {
    let direction = "asc";
    if (sortConfig.key === columnName && sortConfig.direction === "asc") {
      direction = "desc"; // Toggle sorting direction
    }

    // Sorting logic
    const sorted = [...dataList].sort((a, b) => {
      if (a[columnName] < b[columnName]) return direction === "asc" ? -1 : 1;
      if (a[columnName] > b[columnName]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setDataList(sorted); // Update the sorted data
    setSortConfig({ key: columnName, direction }); // Update the sort configuration
  };

  const handleMergeSort = (subColumnName) => {
    handleSort(subColumnName); // Call the general sorting function for each sub-column
  };

  const formatNumber = (number, percent) => {
    let formatData = number;


    if (typeof formatData === "number") {
      formatData = formatData.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS });

      if (percent) {
        formatData += "%";
      }
    }
    return formatData;
  };

  const handleRowClick = (row) => {
    // const catalogItem = Catalogs_MAP.find(catalog => catalog.id === id);

    // const item = {
    //   "access_path": `${catalogItem.path}?id=${row.ItemCode}`,
    //   "header": row.ItemName,
    //   "identifier": row.ItemCode
    // }

    // setSelectedItem(item);
    // setShowModal(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDisplayPerPage = (newDisplayPerPage) => {
    setDisplayPerPage(newDisplayPerPage);
    setCurrentPage(1);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const filteredDataList = useMemo(() => {
    const lowercasedQuery = search.toLowerCase();

    const filteredData = dataList.filter(item =>
      item.ItemCode.toLowerCase().includes(lowercasedQuery) ||
      item.ItemName.toLowerCase().includes(lowercasedQuery)
    );

    const start = (currentPage - 1) * displayPerPage;
    const end = start + displayPerPage;

    const totalItems = filteredData.length;

    setCurrentPage(currentPage);
    setDisplayPerPage(displayPerPage);
    setTotalItems(totalItems);

    return filteredData.slice(start, end);
  }, [dataList, search, currentPage, displayPerPage]);

  return (
    <div className="tauri-historical-panel">
      {loading ? (
        <TauriLoader borderSize={'0.2rem'} widthSize={'2.5rem'} heightSize={'2.5rem'} />
      ) : dataList.length > 0 ? (
        <>
          <div className="mb-3">
            <div className="form-group d-flex align-items-center">
              <input
                type="text"
                id="searchInput"
                placeholder="Search Product..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="form-control"
                aria-label="Search"
              />
            </div>
          </div>
          <table border="1" className="historical-table">
            <thead>
              <tr>
                {tableConfig.binding.data.columns.map((col, index) => {
                  const isSortable = col.sortable !== false;

                  if (col.group) {
                    return (
                      <th
                        key={index}
                        colSpan={col.group.columns.length}
                        style={{
                          textAlign: "center",
                          width: columnWidths[index] || "auto"
                        }}
                      >
                        {col.group.name}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(index, e)}
                        ></div>
                      </th>
                    );
                  }
                  return (
                    <th
                      key={index}
                      rowSpan={2}
                      style={{
                        cursor: isSortable ? "pointer" : "default",
                        textAlign: col.headerAlign || "center",
                        width: columnWidths[index] || "auto",
                        position: "relative",
                      }}
                      onClick={isSortable ? () => handleSort(col.columnName) : undefined}
                    >
                      {col.name}
                      {sortConfig.key === col.columnName ? (
                        sortConfig.direction === "asc" ? " ▲" : " ▼"
                      ) : null}
                      <div
                        className="resize-handle"
                        onMouseDown={(e) => handleMouseDown(index, e)}
                      ></div>
                    </th>
                  );
                })}
              </tr>
              <tr>
                {tableConfig.binding.data.columns.map((col, index) => {
                  if (col.group) {
                    return col.group.columns.map((subCol, subIndex) => (
                      <th
                        key={subIndex}
                        rowSpan={1}
                        style={{
                          cursor: "pointer",
                          textAlign: "center",
                          width: columnWidths[`${index}-${subIndex}`] || "auto",
                          position: "relative",
                        }}
                        onClick={() => handleMergeSort(subCol.columnName)}
                      >
                        {subCol.name}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) =>
                            handleMouseDown(`${index}-${subIndex}`, e)
                          }
                        ></div>
                        {sortConfig.key === subCol.columnName ? (
                          sortConfig.direction === "asc" ? " ▲" : " ▼"
                        ) : null}
                      </th>
                    ));
                  }
                  return null;
                })}
              </tr>
            </thead>
            <tbody>
              {filteredDataList.map((row, index) => (
                <tr key={index} onClick={() => handleRowClick(row)} style={{ '--table-hierarchy-hover-color': palettes[2] }}>
                  {tableConfig.binding.data.columns.map((col, idx) => {
                    if (col.group) {
                      return col.group.columns.map((subCol, subIndex) => (
                        <td key={subIndex} style={{ textAlign: subCol.align || "left", width: columnWidths[idx] || "auto" }}>
                          {formatNumber(row[subCol.columnName], subCol.percent)}
                        </td>
                      ));
                    }
                    return (
                      <td
                        key={idx}
                        style={{
                          textAlign: col.align || "left",
                          width: columnWidths[idx] || "auto",
                        }}
                      >
                        {formatNumber(row[col.columnName], col.percent)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <TauriCustomPaganation
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            displayPerPage={displayPerPage}
            onDisplayPerPage={handleDisplayPerPage}
            totalItems={totalItems}
          />
        </>
      ) : (
        <p>No data available</p>
      )}
      <TauriSearchResultDetailModal
        show={showModal}
        onHide={handleCloseModal}
        item={selectedItem}
      />
    </div>
  );

});

export default TauriHistoricalTableVisual;
